import { Fragment, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Avatar, Card, Stack, styled, SvgIcon, Typography } from '@mui/material';

import { RoundedButton } from '@/components/buttons';
import { Header } from '@/layouts/Header';
import { PageContainer } from '@/layouts/Page';
import { useOrdersStoreActions } from '@/pages/orders/useOrdersStore';
import { ReactComponent as AttachmentFileIcon } from '@/shared/assets/icons-2.0/attach_file.svg';
import { ReactComponent as InfoIcon } from '@/shared/assets/icons-2.0/info.svg';
import { OrdersPaths } from '@/shared/lib/react-router';

import { ReconciliationUploadConfirmationDialog } from '../dialogs/ReconciliationUploadConfirmationDialog';
import {
  resetReconciliationFileStore,
  useReconciliationFile,
  useReconciliationFileStoreActions,
  useReconciliationOrderGuid,
  useReconciliationOrdersPeriod,
} from '../reconciliation-file.store';
import { useRedirectIfNoReconciliationData } from '../useRedirectIfNoReconciliationData';
import { ReconciliationFieldMatchingForm } from './ReconciliationFieldMatchingForm';

const StyledFileToolbar = styled(Card)(({ theme }) => ({
  width: '100%',
  minHeight: '72px',
  flexShrink: 1,
  padding: theme.spacing(2),
  paddingLeft: theme.spacing(3),
})) as typeof Card;

const StyledInstructionPanel = styled(Stack)(({ theme }) => ({
  width: '100%',
  minHeight: '72px',
  flexShrink: 1,
  padding: theme.spacing(2),
  paddingLeft: theme.spacing(4),
})) as typeof Stack;

const FileInfoBar = () => {
  const { t } = useTranslation('orders-page');
  const navigate = useNavigate();
  const reconciliationFile = useReconciliationFile();
  const orderGuid = useReconciliationOrderGuid();
  const ordersPeriod = useReconciliationOrdersPeriod();
  const { openUploadConfirmationDialog } = useReconciliationFileStoreActions();
  const { setPendingReconciliationUploadOrderGuid, setPeriod } = useOrdersStoreActions();

  const handleUploadConfirm = useCallback(() => {
    setPendingReconciliationUploadOrderGuid(orderGuid);

    if (ordersPeriod.length === 2) {
      setPeriod(ordersPeriod);
    }

    resetReconciliationFileStore();
    navigate(OrdersPaths.ROOT);
  }, [orderGuid, ordersPeriod, setPendingReconciliationUploadOrderGuid, setPeriod, navigate]);

  return (
    <Fragment>
      <Stack direction="row" alignItems="center" sx={{ p: 1, pb: 0 }}>
        <StyledFileToolbar
          component={Stack}
          variant="outlined"
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={5}
        >
          <Stack direction="row" flex={1} justifyContent="space-between" alignItems="center" spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Avatar>
                <SvgIcon>
                  <AttachmentFileIcon />
                </SvgIcon>
              </Avatar>
              <Typography component="div" variant="Headline/Bold/small">
                {reconciliationFile?.name ?? ''}
              </Typography>
            </Stack>
          </Stack>
          <RoundedButton variant="outlined" onClick={openUploadConfirmationDialog}>
            {t('reconciliation.field-matching.upload-file')}
          </RoundedButton>
        </StyledFileToolbar>
      </Stack>
      <ReconciliationUploadConfirmationDialog onConfirm={handleUploadConfirm} />
    </Fragment>
  );
};

const InstructionPanel = () => {
  const { t } = useTranslation('orders-page');

  return (
    <StyledInstructionPanel direction="row" spacing={1} alignItems="center">
      <Avatar sx={{ bgcolor: 'background.paper' }}>
        <SvgIcon component={InfoIcon} sx={{ color: 'tetriary.main', justifyContent: 'center', alignItems: 'center' }} />
      </Avatar>
      <Stack>
        <Typography variant="Body/small" color="neutral.700">
          {t('reconciliation.field-matching.instructions.step-1')}
        </Typography>
        <Typography variant="Body/small" color="neutral.700">
          {t('reconciliation.field-matching.instructions.step-2')}{' '}
          <Typography component="span" variant="Body/Bold/small">
            {t('reconciliation.field-matching.instructions.step-2-list')}
          </Typography>
        </Typography>
      </Stack>
    </StyledInstructionPanel>
  );
};

const ReconciliationFieldMatchingPage = () => {
  const { t } = useTranslation('orders-page');
  const shouldRedirect = useRedirectIfNoReconciliationData();

  if (shouldRedirect) return null;

  return (
    <PageContainer title={t('reconciliation.field-matching.page-title')}>
      <Header title={t('reconciliation.field-matching.page-title')} />
      <FileInfoBar />
      <InstructionPanel />
      <ReconciliationFieldMatchingForm />
    </PageContainer>
  );
};

export default ReconciliationFieldMatchingPage;
